import type {
  AddUnconnectedAccountRequest,
  AddUnconnectedAccountResponse,
} from '@/actions/financialAccountActions'
import type { WidgetDisplay } from '../../lib/types'

export type AccountKind = AddUnconnectedAccountRequest['accountKind']

export const ACCOUNT_KINDS: { value: AccountKind; label: string }[] = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'credit_card', label: 'Credit card' },
]

export const TRANSACTION_COUNTS = {
  few: 'few',
  many: 'many',
} as const

export type TransactionCount =
  (typeof TRANSACTION_COUNTS)[keyof typeof TRANSACTION_COUNTS]

export const TRANSACTION_COUNT_OPTIONS: {
  value: TransactionCount
  label: string
}[] = [
  { value: TRANSACTION_COUNTS.few, label: '10 or fewer' },
  { value: TRANSACTION_COUNTS.many, label: 'More than 10' },
]

export const MAX_BANK_NAME_LENGTH = 60

export interface UnconnectedAccountAnswers {
  bank: string
  lastFour: string
  kind: AccountKind | null
  count: TransactionCount | null
}

export const EMPTY_ANSWERS: UnconnectedAccountAnswers = {
  bank: '',
  lastFour: '',
  kind: null,
  count: null,
}

const isLastFour = (value: string): boolean => /^\d{4}$/.test(value)

export const isComplete = (answers: UnconnectedAccountAnswers): boolean =>
  answers.bank.trim().length > 0 &&
  isLastFour(answers.lastFour) &&
  answers.kind !== null &&
  answers.count !== null

export const toFewOutcome = (
  answers: UnconnectedAccountAnswers
): { text: string; display: WidgetDisplay } => {
  const named = `${answers.bank.trim()} ••${answers.lastFour}`
  return {
    text: `${named} has 10 or fewer practice transactions since their catch-up period began. No account was added: they will enter those transactions by hand.`,
    display: {
      label: named,
      meta: '10 or fewer transactions',
    },
  }
}

export const toManyOutcome = (
  result: AddUnconnectedAccountResponse
): { text: string; display: WidgetDisplay } => {
  const added =
    result.outcome === 'reused'
      ? `${result.accountLabel} was already on file.`
      : `${result.accountLabel} was added.`
  return {
    text: `${added} It has more than 10 practice transactions since their catch-up period began, so Heard needs its statements.`,
    display: { label: result.accountLabel, meta: 'Statements needed' },
  }
}
